import React from 'react';
import { useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiArrowLeft,
  FiUser,
  FiBriefcase,
  FiPrinter,
  FiDownload,
  FiCheckCircle,
  FiAlertTriangle,
  FiRefreshCw,
} from 'react-icons/fi';
import { registrationService } from '../../services/apiService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatDate, formatDateTime } from '../../utils/formatters';
import { exportPDF } from '../../utils/exporters';

const RegistrationDetail = () => {
  const { registrationId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch, isRefetching } = useQuery(
    ['registration-detail', registrationId],
    () => registrationService.getById(registrationId),
    { enabled: !!registrationId }
  );

  if (isLoading) return <LoadingSpinner message="Loading registration..." />;

  if (error) {
    return (
      <div>
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              className="icon-btn"
              onClick={() => navigate('/lra/registration/history')}
            >
              <FiArrowLeft size={20} />
            </button>
            <h1 className="page-title">Registration Not Found</h1>
          </div>
        </div>
        <div className="card">
          <div className="empty-state">
            <FiAlertTriangle size={48} color="#ef4444" />
            <h3>Unable to load registration</h3>
            <p>{error.message}</p>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/lra/registration/history')}
            >
              Back to History
            </button>
          </div>
        </div>
      </div>
    );
  }

  const payload = data?.data || {};
  const registration = payload.registration || {};
  const user = payload.user || {};
  const business = payload.business || {};
  const tempPassword = payload.temporary_password || '12345';

  const isSuccess = registration.status === 'success';

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    try {
      exportPDF({
        title: 'Taxpayer Registration Certificate',
        subtitle: `Registration ID: ${registration.registration_id}`,
        summaryRows: [
          {
            label: 'Full Name',
            value: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
          },
          { label: 'DSSN', value: registration.dssn || '—' },
          { label: 'TIN', value: registration.tin || '—' },
          { label: 'Business Name', value: business.business_name || '—' },
          { label: 'Business ID', value: business.business_id || '—' },
          {
            label: 'Tax Division',
            value: (business.tax_division || '—').replace(/_/g, ' '),
          },
          { label: 'Email (login)', value: user.email || '—' },
          { label: 'Phone', value: user.phone || '—' },
          { label: 'Temporary Password', value: tempPassword },
          {
            label: 'Registered By',
            value: registration.registered_by_officer_id || '—',
          },
          {
            label: 'Registered At',
            value: formatDateTime(registration.created_at),
          },
        ],
        columns: [
          { key: 'label', label: 'Field' },
          { key: 'value', label: 'Value' },
        ],
        rows: [
          { label: 'Registration ID', value: registration.registration_id },
          { label: 'DSSN (login)', value: registration.dssn },
          { label: 'TIN', value: registration.tin },
          { label: 'Email (login)', value: user.email },
          { label: 'Temporary Password', value: tempPassword },
        ],
        filename: `LRA-registration-${registration.dssn}.pdf`,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export PDF');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            className="icon-btn"
            onClick={() => navigate('/lra/registration/history')}
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title">Registration Details</h1>
            <p
              className="page-subtitle"
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
            >
              {registration.registration_id}
            </p>
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <FiRefreshCw size={18} />
            Refresh
          </button>
          <button className="btn btn-secondary" onClick={handlePrint}>
            <FiPrinter size={18} />
            Print
          </button>
          <button className="btn btn-primary" onClick={handleDownloadPDF}>
            <FiDownload size={18} />
            Download PDF
          </button>
        </div>
      </div>

      {/* Status banner */}
      <div
        style={{
          background: isSuccess ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${isSuccess ? '#86efac' : '#fca5a5'}`,
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        {isSuccess ? (
          <FiCheckCircle size={22} color="#16a34a" />
        ) : (
          <FiAlertTriangle size={22} color="#dc2626" />
        )}
        <div>
          <div
            style={{
              fontWeight: 600,
              color: isSuccess ? '#166534' : '#991b1b',
            }}
          >
            {isSuccess ? 'Registration Successful' : `Status: ${registration.status}`}
          </div>
          <div
            style={{
              fontSize: '13px',
              color: isSuccess ? '#15803d' : '#b91c1c',
              marginTop: 2,
            }}
          >
            {formatDateTime(registration.created_at)}
            {registration.registered_by_officer_id &&
              ` • By ${registration.registered_by_officer_id}`}
          </div>
        </div>
      </div>

      {/* Credentials card (printable) */}
      {isSuccess && (
        <div
          id="credentials-card"
          className="card"
          style={{
            marginBottom: '24px',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
          }}
        >
          <h2
            style={{
              fontSize: '18px',
              fontWeight: 700,
              marginBottom: '16px',
            }}
          >
            Login Credentials
          </h2>
          <div className="form-row">
            <CredentialBlock
              label="DSSN (username)"
              value={registration.dssn}
              highlight
            />
            <CredentialBlock
              label="TIN"
              value={registration.tin}
              highlight
            />
          </div>
          <div className="form-row" style={{ marginTop: '16px' }}>
            <CredentialBlock label="Email (login)" value={user.email} />
            <CredentialBlock
              label="Temporary Password"
              value={tempPassword}
              highlight
            />
          </div>
        </div>
      )}

      {/* Two-column details */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: '24px',
        }}
      >
        {/* Personal info */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              <FiUser
                size={18}
                style={{ verticalAlign: 'middle', marginRight: 8 }}
              />
              Personal Information
            </h2>
          </div>
          <InfoRow label="Full Name" value={
            `${user.first_name || ''} ${user.last_name || ''}`.trim() || '—'
          } />
          <InfoRow label="Email" value={user.email || '—'} />
          <InfoRow label="Phone" value={user.phone || '—'} />
          <InfoRow
            label="Date of Birth"
            value={user.DATE_OF_BIRTH ? formatDate(user.DATE_OF_BIRTH) : '—'}
          />
          <InfoRow label="Place of Birth" value={user.PLACE_OF_BIRTH || '—'} />
          <InfoRow label="Nationality" value={user.NATIONALITY || '—'} />
          <InfoRow label="Sex" value={user.SEX || '—'} />
          <InfoRow
            label="Marital Status"
            value={user.MARITAL_STATUS || '—'}
          />
          <InfoRow
            label="Employment"
            value={user.EMPLOYMENT_STATUS || '—'}
          />
          <InfoRow
            label="Children"
            value={user.NUMBER_OF_CHILDREN ?? '—'}
          />
          <InfoRow label="Address" value={user.address || '—'} />
          <InfoRow
            label="Postal Address"
            value={user.postal_address || '—'}
          />
        </div>

        {/* Business info */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              <FiBriefcase
                size={18}
                style={{ verticalAlign: 'middle', marginRight: 8 }}
              />
              Business Information
            </h2>
          </div>
          <InfoRow
            label="Business Name"
            value={business.business_name || '—'}
          />
          <InfoRow
            label="Business ID"
            value={business.business_id || '—'}
            mono
          />
          <InfoRow label="TIN" value={business.tin || '—'} mono />
          <InfoRow
            label="Tax Division"
            value={(business.tax_division || '—').replace(/_/g, ' ')}
          />
          <InfoRow
            label="Verification"
            value={business.verification_status || '—'}
          />
          <InfoRow
            label="Verified At"
            value={
              business.verified_at
                ? formatDateTime(business.verified_at)
                : '—'
            }
          />
          <InfoRow
            label="Verified By"
            value={business.verified_by || '—'}
          />
          <div style={{ borderTop: '1px solid #f3f4f6', margin: '8px 0' }} />
          <InfoRow
            label="Registration Status"
            value={registration.status}
          />
          <InfoRow
            label="Registered By"
            value={registration.registered_by_officer_id || '—'}
            mono
          />
          {registration.notes && (
            <InfoRow label="Notes" value={registration.notes} />
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Helpers ----------
const InfoRow = ({ label, value, mono }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: '1px solid #f3f4f6',
      fontSize: '14px',
      gap: 12,
    }}
  >
    <span style={{ color: '#6b7280', flexShrink: 0 }}>{label}</span>
    <span
      style={{
        fontWeight: 500,
        fontFamily: mono ? 'monospace' : 'inherit',
        fontSize: mono ? '12px' : 'inherit',
        textAlign: 'right',
        wordBreak: 'break-word',
      }}
    >
      {value}
    </span>
  </div>
);

const CredentialBlock = ({ label, value, highlight }) => (
  <div
    style={{
      background: highlight ? '#eff6ff' : '#fff',
      border: `1px solid ${highlight ? '#93c5fd' : '#e5e7eb'}`,
      borderRadius: '8px',
      padding: '16px',
    }}
  >
    <div
      style={{
        fontSize: '11px',
        fontWeight: 700,
        color: '#6b7280',
        textTransform: 'uppercase',
        marginBottom: '6px',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: '18px',
        fontWeight: 700,
        fontFamily: 'monospace',
        color: highlight ? '#1a73e8' : '#1f2937',
      }}
    >
      {value || '—'}
    </div>
  </div>
);

export default RegistrationDetail;
